import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowPathIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useSalesDashboard } from '../../hooks/useSales';
import { SalesDashboardData, SalesDashboardRange } from '../../types/sales.types';

const RANGE_OPTIONS: { value: SalesDashboardRange; label: string; comparison: string }[] = [
  { value: '30d', label: '30 days', comparison: 'vs previous 30 days' },
  { value: '90d', label: '90 days', comparison: 'vs previous 90 days' },
  { value: '12m', label: '12 months', comparison: 'vs previous 12 months' },
  { value: 'all', label: 'All time', comparison: '' },
];

// The order of an order's life, used to list statuses in a sensible sequence
const STATUS_ORDER = ['quote', 'order', 'confirmed', 'partially_shipped', 'shipped', 'delivered', 'completed', 'cancelled'];

const STATUS_META: Record<string, { label: string; color: string; bar: string }> = {
  quote: { label: 'Quote', color: 'text-gray-700 bg-gray-50 ring-gray-500/20', bar: 'bg-gray-400' },
  order: { label: 'Order', color: 'text-blue-700 bg-blue-50 ring-blue-600/20', bar: 'bg-blue-500' },
  confirmed: { label: 'Confirmed', color: 'text-indigo-700 bg-indigo-50 ring-indigo-600/20', bar: 'bg-indigo-500' },
  partially_shipped: { label: 'Partly shipped', color: 'text-amber-800 bg-amber-50 ring-amber-600/20', bar: 'bg-amber-500' },
  shipped: { label: 'Shipped', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20', bar: 'bg-yellow-500' },
  delivered: { label: 'Delivered', color: 'text-green-700 bg-green-50 ring-green-600/20', bar: 'bg-green-500' },
  completed: { label: 'Completed', color: 'text-green-700 bg-green-50 ring-green-600/20', bar: 'bg-green-500' },
  cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50 ring-red-600/20', bar: 'bg-red-400' },
};

const FALLBACK_STATUS = { label: '', color: 'text-gray-700 bg-gray-50 ring-gray-500/20', bar: 'bg-gray-400' };
const badgeClass = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset';
const cardClass = 'rounded-lg bg-white shadow-sm ring-1 ring-gray-200';

const formatNumber = (value: number) => Number(value || 0).toLocaleString('en-IN');

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatCompactCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(amount || 0));

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatMonth = (month: string) => {
  const [year, monthIndex] = month.split('-').map(Number);
  return new Date(year, monthIndex - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

const pluralize = (count: number, singular: string, plural: string) =>
  `${formatNumber(count)} ${count === 1 ? singular : plural}`;

const RevenueTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="mt-1 text-sm text-gray-700">
        Revenue: <span className="font-medium text-gray-900">{formatCurrency(row.revenue)}</span>
      </p>
      <p className="text-sm text-gray-700">{pluralize(row.orders, 'order', 'orders')}</p>
    </div>
  );
};

const RankedList: React.FC<{
  title: string;
  description: string;
  rows: { key: string; name: string; detail: string; value: number }[];
  emptyText: string;
}> = ({ title, description, rows, emptyText }) => {
  const maxValue = Math.max(...rows.map((row) => row.value), 0);

  return (
    <div className={`${cardClass} p-5`}>
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      {rows.length === 0 ? (
        <p className="mt-6 py-6 text-center text-sm text-gray-500">{emptyText}</p>
      ) : (
        <ol className="mt-4 space-y-4">
          {rows.map((row, index) => (
            <li key={row.key}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate font-medium text-gray-900">
                  <span className="mr-2 text-gray-400 tabular-nums">{index + 1}</span>
                  {row.name}
                </span>
                <span className="shrink-0 font-medium text-gray-900 tabular-nums">
                  {formatCurrency(row.value)}
                </span>
              </div>
              <p className="mt-0.5 pl-5 text-xs text-gray-500">{row.detail}</p>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100">
                <div
                  className="h-1.5 rounded-full bg-indigo-500"
                  style={{ width: `${maxValue > 0 ? (row.value / maxValue) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const DashboardSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-6" aria-hidden="true">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className={`${cardClass} h-28`} />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div className={`${cardClass} h-96 xl:col-span-2`} />
      <div className={`${cardClass} h-96`} />
    </div>
  </div>
);

const SalesDashboard: React.FC = () => {
  const [range, setRange] = useState<SalesDashboardRange>('30d');
  const { data, isLoading, isFetching, error, refetch } = useSalesDashboard(range);

  const rangeOption = RANGE_OPTIONS.find((option) => option.value === range) || RANGE_OPTIONS[0];
  const periodLabel = range === 'all' ? 'all time' : `last ${rangeOption.label}`;

  const renderContent = (dashboard: SalesDashboardData) => {
    const { revenue, deliveryPerformance } = dashboard;

    const chartData = dashboard.monthlyRevenue.map((row) => ({ ...row, label: formatMonth(row.month) }));
    const hasChartData = chartData.some((row) => row.revenue > 0);

    const statusRows = [...dashboard.ordersByStatus].sort(
      (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
    );
    const maxStatusCount = Math.max(...statusRows.map((row) => row.count), 0);
    const totalOrders = statusRows.reduce((sum, row) => sum + row.count, 0);

    const isUp = (revenue.changePercent ?? 0) >= 0;
    const ChangeIcon = isUp ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;

    return (
      <div className={`space-y-6 ${isFetching ? 'opacity-70' : ''}`}>
        {/* Key figures */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Revenue</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <BanknotesIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">{formatCurrency(revenue.value)}</p>
            {!rangeOption.comparison ? (
              <p className="mt-1 text-sm text-gray-500">All orders on record</p>
            ) : revenue.changePercent === null ? (
              <p className="mt-1 text-sm text-gray-500">No earlier period to compare</p>
            ) : (
              <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-gray-500">
                <span className={`inline-flex items-center font-medium ${isUp ? 'text-green-700' : 'text-red-700'}`}>
                  <ChangeIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {isUp ? '+' : ''}
                  {revenue.changePercent.toFixed(1)}%
                </span>
                {rangeOption.comparison}
              </p>
            )}
          </div>

          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Orders</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <ShoppingCartIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">{formatNumber(revenue.orders)}</p>
            <p className="mt-1 text-sm text-gray-500">
              Average {formatCurrency(dashboard.averageOrderValue)} per order
            </p>
          </div>

          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Open orders</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-yellow-50 text-yellow-600">
                <ClockIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">{formatNumber(dashboard.openOrders)}</p>
            <p className="mt-1 text-sm text-gray-500">
              {dashboard.overdueOrders > 0 ? (
                <span className="font-medium text-red-600">
                  {formatNumber(dashboard.overdueOrders)} past the delivery date
                </span>
              ) : (
                'None past the delivery date'
              )}
            </p>
          </div>

          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">Open quotes</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-50 text-green-600">
                <DocumentTextIcon className="h-5 w-5" />
              </span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">{formatNumber(dashboard.quotes.count)}</p>
            <p className="mt-1 text-sm text-gray-500">Worth {formatCurrency(dashboard.quotes.value)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Monthly revenue */}
          <div className={`${cardClass} p-5 xl:col-span-2`}>
            <h2 className="text-base font-semibold text-gray-900">Revenue by month</h2>
            <p className="mt-1 text-sm text-gray-500">
              Last 12 months, by order date. Quotes and cancelled orders are excluded.
            </p>
            {hasChartData ? (
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatCompactCurrency}
                      width={64}
                    />
                    <Tooltip content={<RevenueTooltip />} cursor={{ fill: '#f9fafb' }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#4f46e5" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="mt-4 flex h-72 flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 text-center">
                <BanknotesIcon className="h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm font-medium text-gray-900">No revenue in the last 12 months</p>
                <p className="mt-1 text-sm text-gray-500">Monthly totals will appear here as orders are placed.</p>
              </div>
            )}
          </div>

          {/* Orders by status */}
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Orders by status</h2>
              <Link to="/sales" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                View
              </Link>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              All {pluralize(totalOrders, 'order', 'orders')} on record, including quotes.
            </p>
            {statusRows.length === 0 ? (
              <p className="mt-6 py-6 text-center text-sm text-gray-500">No orders yet.</p>
            ) : (
              <ul className="mt-4 space-y-4">
                {statusRows.map((row) => {
                  const meta = STATUS_META[row.status] || { ...FALLBACK_STATUS, label: row.status };
                  return (
                    <li key={row.status}>
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className={`${badgeClass} ${meta.color}`}>{meta.label}</span>
                        <span className="text-gray-700 tabular-nums">
                          <span className="font-semibold text-gray-900">{formatNumber(row.count)}</span> ·{' '}
                          {formatCurrency(row.value)}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100">
                        <div
                          className={`h-1.5 rounded-full ${meta.bar}`}
                          style={{ width: `${maxStatusCount > 0 ? (row.count / maxStatusCount) * 100 : 0}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <RankedList
            title="Top customers"
            description={`By revenue, ${periodLabel}.`}
            rows={dashboard.topCustomers.map((row) => ({
              key: row.id,
              name: row.name,
              detail: pluralize(row.orders, 'order', 'orders'),
              value: row.revenue,
            }))}
            emptyText="No customer orders in this period."
          />

          <RankedList
            title="Top products"
            description={`By revenue, ${periodLabel}.`}
            rows={dashboard.topProducts.map((row) => ({
              key: row.productId,
              name: row.name,
              detail: `${formatNumber(row.quantity)} sold`,
              value: row.revenue,
            }))}
            emptyText="No product sales in this period."
          />

          {/* Delivery performance */}
          <div className={`${cardClass} p-5`}>
            <h2 className="text-base font-semibold text-gray-900">Delivery performance</h2>
            <p className="mt-1 text-sm text-gray-500">Delivered orders against the date promised.</p>
            {deliveryPerformance.deliveredOrders === 0 ? (
              <div className="mt-6 flex flex-col items-center py-6 text-center">
                <CheckCircleIcon className="h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm font-medium text-gray-900">No deliveries recorded yet</p>
                <p className="mt-1 text-sm text-gray-500">
                  On-time figures appear once orders are marked as delivered.
                </p>
              </div>
            ) : (
              <>
                <p className="mt-4 text-3xl font-semibold text-gray-900 tabular-nums">
                  {deliveryPerformance.onTimePercentage?.toFixed(0)}%
                  <span className="ml-2 text-sm font-normal text-gray-500">delivered on time</span>
                </p>
                <div className="mt-3 h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-green-500"
                    style={{ width: `${deliveryPerformance.onTimePercentage ?? 0}%` }}
                  />
                </div>
                <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-gray-50 px-2 py-2">
                    <dd className="text-lg font-semibold text-gray-900 tabular-nums">
                      {formatNumber(deliveryPerformance.deliveredOrders)}
                    </dd>
                    <dt className="text-xs text-gray-500">Delivered</dt>
                  </div>
                  <div className="rounded-md bg-gray-50 px-2 py-2">
                    <dd className="text-lg font-semibold text-green-700 tabular-nums">
                      {formatNumber(deliveryPerformance.onTime)}
                    </dd>
                    <dt className="text-xs text-gray-500">On time</dt>
                  </div>
                  <div className="rounded-md bg-gray-50 px-2 py-2">
                    <dd
                      className={`text-lg font-semibold tabular-nums ${
                        deliveryPerformance.late > 0 ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {formatNumber(deliveryPerformance.late)}
                    </dd>
                    <dt className="text-xs text-gray-500">Late</dt>
                  </div>
                </dl>
                {deliveryPerformance.averageDelayDays !== null && (
                  <p className="mt-3 text-sm text-gray-600">
                    Late deliveries were {deliveryPerformance.averageDelayDays.toFixed(1)} days late on average.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Recent orders */}
          <div className={`${cardClass} overflow-hidden xl:col-span-2`}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Recent orders</h2>
                <p className="mt-1 text-sm text-gray-500">The latest sales orders and quotes.</p>
              </div>
              <Link to="/sales" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                View all
              </Link>
            </div>
            {dashboard.recentOrders.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-gray-500">No orders yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Order', 'Customer', 'Order Date', 'Items', 'Status'].map((heading) => (
                        <th
                          key={heading}
                          className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                        >
                          {heading}
                        </th>
                      ))}
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {dashboard.recentOrders.map((order) => {
                      const meta = STATUS_META[order.status] || { ...FALLBACK_STATUS, label: order.status };
                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-5 py-3 text-sm">
                            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                              {order.orderNumber}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{order.customerName}</td>
                          <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-700">
                            {formatDate(order.orderDate)}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3 text-sm text-gray-700">
                            {formatNumber(order.itemCount)}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3 text-sm">
                            <span className={`${badgeClass} ${meta.color}`}>{meta.label}</span>
                          </td>
                          <td className="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                            {formatCurrency(order.grandTotal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Overdue deliveries */}
          <div className={`${cardClass} p-5`}>
            <h2 className="text-base font-semibold text-gray-900">Overdue deliveries</h2>
            <p className="mt-1 text-sm text-gray-500">Open orders past their expected delivery date.</p>
            {dashboard.overdueList.length === 0 ? (
              <div className="mt-6 flex flex-col items-center py-6 text-center">
                <CheckCircleIcon className="h-10 w-10 text-green-500" />
                <p className="mt-2 text-sm font-medium text-gray-900">Nothing is overdue</p>
                <p className="mt-1 text-sm text-gray-500">Every open order is still within its date.</p>
              </div>
            ) : (
              <>
                <ul className="mt-4 divide-y divide-gray-100">
                  {dashboard.overdueList.map((order) => (
                    <li key={order.id} className="flex items-start gap-3 py-3">
                      <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">{order.customerName}</p>
                        <p className="text-xs text-gray-500">
                          <span className="font-mono">{order.orderNumber}</span> · due{' '}
                          {formatDate(order.expectedDeliveryDate)}
                        </p>
                      </div>
                      <p className="shrink-0 text-right text-sm">
                        <span className="font-semibold text-red-600">
                          {pluralize(order.daysOverdue, 'day', 'days')} late
                        </span>
                        <span className="block text-xs text-gray-500 tabular-nums">
                          {formatCurrency(order.grandTotal)}
                        </span>
                      </p>
                    </li>
                  ))}
                </ul>
                {dashboard.overdueOrders > dashboard.overdueList.length && (
                  <Link to="/sales" className="mt-3 inline-block text-sm font-medium text-indigo-600 hover:text-indigo-500">
                    View all {formatNumber(dashboard.overdueOrders)} overdue orders
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {/* Shortcuts */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { to: '/sales', icon: ShoppingCartIcon, title: 'Manage orders', text: 'View and update sales orders' },
            { to: '/quotes', icon: DocumentTextIcon, title: 'Manage quotes', text: 'Create quotes and convert them' },
            { to: '/customers', icon: UsersIcon, title: 'Manage customers', text: 'Add and edit customer information' },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`${cardClass} flex items-center gap-3 px-5 py-4 hover:ring-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <item.icon className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{item.title}</span>
                <span className="block text-sm text-gray-500">{item.text}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Sales Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Sales performance and key metrics. Revenue and rankings cover {periodLabel}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Reporting period">
            {RANGE_OPTIONS.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setRange(option.value)}
                aria-pressed={range === option.value}
                className={`border px-3 py-2 text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                  index === 0 ? 'rounded-l-md' : '-ml-px'
                } ${index === RANGE_OPTIONS.length - 1 ? 'rounded-r-md' : ''} ${
                  range === option.value
                    ? 'z-10 border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <DashboardSkeleton />
      ) : error || !data ? (
        <div className={`${cardClass} px-6 py-12 text-center`}>
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
          <p className="mt-2 text-sm font-medium text-gray-900">The sales dashboard could not be loaded</p>
          <p className="mt-1 text-sm text-gray-500">Check your connection and try again.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          {renderContent(data)}
          <p className="text-xs text-gray-500">
            Updated{' '}
            {new Date(data.generatedAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </>
      )}
    </div>
  );
};

export default SalesDashboard;
